import * as yup from 'yup';
import { useTranslate } from '@tolgee/react';
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3';
import React, { useCallback, useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { UnionFragment } from '@amf/shared/types/graphql';
import TextField from '@amf/shared/components/form/TextField';
import CheckboxField from '@amf/shared/components/form/CheckboxField';
import SelectField from '@amf/shared/components/form/SelectField';
import TextArea from '@amf/shared/components/form/TextArea';
import Button from '@amf/shared/components/button/Button';

const schema = yup.object({
  name: yup
    .string()
    .required('join.referee.form.name.required')
    .max(64, 'join.referee.form.name.max'),
  identification: yup
    .string()
    .required('join.referee.form.identification.required')
    .max(11, 'join.referee.form.identification.invalid')
    .min(8, 'join.referee.form.identification.invalid'),
  foreign: yup.boolean(),
  union: yup.string().required('join.referee.form.union.required'),
  email: yup
    .string()
    .email('join.referee.form.email.invalid')
    .required('join.referee.form.email.required'),
  phone: yup
    .string()
    .matches(/^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/g, 'join.referee.form.phone.invalid')
    .required('join.referee.form.phone.required'),
  experience: yup
    .string()
    .required('join.referee.form.experience.required')
    .max(512, 'join.referee.form.experience.max'),
});

export interface JoinRefereeFormData {
  name: string;
  identification: string;
  foreign: boolean;
  union: string;
  email: string;
  phone: string;
  experience: string;
}

interface JoinRefereeFormProps {
  unions: UnionFragment[];
  values: JoinRefereeFormData;
  onSubmit: (values: JoinRefereeFormData) => Promise<void>;
}

function JoinRefereeForm({ values, onSubmit, unions }: JoinRefereeFormProps) {
  const { t } = useTranslate('malyfotbal');
  const { executeRecaptcha } = useGoogleReCaptcha();
  const [sending, setSending] = useState(false);
  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<JoinRefereeFormData>({
    resolver: yupResolver(schema),
    defaultValues: values,
  });

  const verifyReCaptcha = useCallback(async () => {
    if (!executeRecaptcha) {
      console.log('Execute recaptcha not yet available');
      return;
    }

    await executeRecaptcha('contactForm');
  }, [executeRecaptcha]);

  useEffect(() => {
    verifyReCaptcha();
  }, [verifyReCaptcha]);

  const saveForm = useCallback(
    async (data: JoinRefereeFormData) => {
      setSending(true);
      await onSubmit(data);
      setSending(false);
    },
    [onSubmit],
  );

  return (
    <div className='Form'>
      <form onSubmit={handleSubmit(saveForm)}>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='name'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.referee.form.name')}
                  error={errors.name?.message}
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='identification'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.referee.form.identification')}
                  error={errors.identification?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='email'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.referee.form.email')}
                  error={errors.email?.message}
                  type='email'
                  {...field}
                />
              )}
            />
          </div>
          <div className='Form__col'>
            <Controller
              name='phone'
              control={control}
              render={({ field }) => (
                <TextField
                  className='Form__field'
                  label={t('join.referee.form.phone')}
                  error={errors.phone?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='foreign'
              control={control}
              render={({ field }) => (
                <CheckboxField type='checkbox' label={t('join.referee.form.foreign')} {...field} />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='union'
              control={control}
              render={({ field }) => (
                <SelectField
                  label={t('join.referee.form.union')}
                  error={errors.union?.message}
                  className='Form__field'
                  {...field}
                >
                  <option value={''}>{t('join.referee.form.union.choose')}</option>
                  {unions.map(({ id, name }) => (
                    <option key={id} value={name}>
                      {name}
                    </option>
                  ))}
                </SelectField>
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Controller
              name='experience'
              control={control}
              render={({ field }) => (
                <TextArea
                  className='Form__field'
                  label={t('join.referee.form.experience')}
                  error={errors.experience?.message}
                  {...field}
                />
              )}
            />
          </div>
        </div>
        <div className='Form__row'>
          <div className='Form__col'>
            <Button type='submit' variant='blue' disabled={sending}>
              {t('join.referee.form.send')}
            </Button>
          </div>
        </div>
      </form>
    </div>
  );
}

export default JoinRefereeForm;
